import { format } from 'date-fns';
import purify from 'dompurify';
import { Edit3 } from 'lucide-react';
import type { JournalEntryType } from 'shared';

import type { JournalEntryDto } from '../../lib/api/endpoints';
import { Button } from '../ui/Button';

interface JournalTimelineProps {
  entries: JournalEntryDto[];
  onEdit?: (entry: JournalEntryDto) => void;
}

const order: Record<JournalEntryType, number> = {
  PRE_TRADE: 0,
  MID_TRADE: 1,
  POST_TRADE: 2,
  REVIEW: 3,
};

export function JournalTimeline({ entries, onEdit }: JournalTimelineProps) {
  const sorted = [...entries].sort((left, right) => {
    const typeOrder = order[left.entryType] - order[right.entryType];
    if (typeOrder !== 0) return typeOrder;
    return left.createdAt.localeCompare(right.createdAt);
  });

  return (
    <ol className="space-y-4 border-l border-border pl-4">
      {sorted.map((entry) => (
        <li key={entry.id} className="relative rounded border border-border bg-surface p-4">
          <span className="bg-accent absolute -left-[25px] top-5 h-3 w-3 rounded-full border border-border" />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="bg-accent/10 text-accent rounded px-2 py-1 text-xs font-semibold">
                {entry.entryType.replace('_', ' ')}
              </span>
              <time className="text-sm text-text-muted">
                {format(new Date(entry.createdAt), 'dd MMM yyyy HH:mm')}
              </time>
            </div>
            {onEdit ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                aria-label={`Edit ${entry.entryType.toLowerCase()} journal entry`}
                onClick={() => onEdit(entry)}
              >
                <Edit3 className="h-4 w-4" />
                Edit
              </Button>
            ) : null}
          </div>
          <div
            className="mt-3 text-sm leading-6 text-text"
            dangerouslySetInnerHTML={{ __html: markdownPreview(entry.body || entry.thesis || '') }}
          />
        </li>
      ))}
    </ol>
  );
}

function markdownPreview(value: string): string {
  const escaped = value
    .slice(0, 400)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');
  const html = escaped
    .replace(/^### (.*)$/gm, '<h3>$1</h3>')
    .replace(/^## (.*)$/gm, '<h2>$1</h2>')
    .replace(/^# (.*)$/gm, '<h1>$1</h1>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br />');
  return purify.sanitize(html, {
    ALLOWED_TAGS: ['br', 'strong', 'em', 'h1', 'h2', 'h3'],
    ALLOWED_ATTR: [],
  });
}
